import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Rating,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarsIcon from '@mui/icons-material/Stars';
import type { PortalContentBlock, PortalMoviePayload } from '../../portal/portalDraftEnvelope';
import { DraftHeroBanner } from './DraftHeroBanner';
import { ArticleBodyEditorJs } from './ArticleBodyEditorJs';
import {
  MOVIE_AGE_RATINGS,
  MOVIE_AVAILABILITY_OPTIONS,
  MOVIE_TAB_LOGO,
  autoGenerateDiscussionQuestions,
  defaultReviewSearchUrl,
  effectiveReviewUrlForSite,
  inferRatingSite,
  normalizeMovieAvailability,
  reviewUrlInputValue,
  saveReviewUrlForSite,
  saveSourceStarRating,
  tabSourceStarRating,
  type MovieRatingSite,
} from '../../utils/movieDraftHelpers';

function escapeBlockText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function questionsToBlocks(questions: string[]): PortalContentBlock[] {
  return questions
    .map((question) => question.trim())
    .filter(Boolean)
    .map((question) => ({
      type: 'paragraph',
      text: question,
      html: `<p>${escapeBlockText(question)}</p>`,
    }));
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fafafa',
    '& fieldset': { borderColor: '#3f3f46' },
    '&:hover fieldset': { borderColor: '#a1a1aa' },
    '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
  },
  '& .MuiInputLabel-root': { color: '#a1a1aa' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
};

const REVIEW_ROW_HEIGHT = 40;

const rowRatingSx = {
  flexShrink: 0,
  '& .MuiRating-icon': { fontSize: '1.35rem' },
  '& .MuiRating-iconFilled': { color: '#fbbf24' },
  '& .MuiRating-iconHover': { color: '#fcd34d' },
};

export type MovieDraftPanelProps = {
  movie: PortalMoviePayload;
  saveMovie: (next: PortalMoviePayload) => void;
  /** Rendered above the writing tabs (e.g. source clusters). */
  sourceClustersSlot?: React.ReactNode;
  /** Creator + access (Library). */
  draftAccessBar?: React.ReactNode;
  accessInHeader?: boolean;
  readOnly?: boolean;
};

export function MovieDraftPanel({
  movie,
  saveMovie,
  sourceClustersSlot,
  draftAccessBar,
  accessInHeader = false,
  readOnly = false,
}: MovieDraftPanelProps) {
  const movieRef = useRef(movie);
  movieRef.current = movie;
  const [detailTab, setDetailTab] = useState(0);
  const [discussionEditorKey, setDiscussionEditorKey] = useState(0);
  const [ratingUiTab, setRatingUiTab] = useState(0);
  useEffect(() => {
    setRatingUiTab(inferRatingSite(movie.rating_source) === 'rotten_tomatoes' ? 1 : 0);
  }, [movie.id, movie.rating_source]);

  useEffect(() => {
    if (!(movie.availability || '').trim()) {
      saveMovie({ ...movie, availability: normalizeMovieAvailability(movie.availability) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- normalize legacy empty once per notebook movie
  }, [movie.id]);

  const handleRatingTabChange = (_: React.SyntheticEvent, value: number) => {
    setRatingUiTab(value);
    if (value === 0) saveMovie({ ...movie, rating_source: 'imdb' });
    if (value === 1) saveMovie({ ...movie, rating_source: 'rotten_tomatoes' });
  };

  const externalSite: MovieRatingSite = ratingUiTab === 1 ? 'rotten_tomatoes' : 'imdb';
  const previewReviewUrl =
    ratingUiTab === 0 || ratingUiTab === 1
      ? effectiveReviewUrlForSite(movie, externalSite) ||
        defaultReviewSearchUrl(externalSite, movie.movie_title)
      : '';

  const openReviewPreview = () => {
    window.open(previewReviewUrl, '_blank', 'noopener,noreferrer');
  };

  const availabilityNorm = normalizeMovieAvailability(movie.availability);
  const titleMissing = !movie.movie_title.trim();
  const urlMissing = !(movie.youtube_url || '').trim();

  return (
    <Box sx={{ minHeight: 500 }}>
      {!accessInHeader ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fafafa', flexShrink: 0 }}>
            Movie draft
          </Typography>
          {draftAccessBar ? (
            <Box sx={{ flex: 1, minWidth: 200, maxWidth: 560, textAlign: 'right' }}>{draftAccessBar}</Box>
          ) : null}
        </Box>
      ) : null}
      <DraftHeroBanner
        imageUrl={movie.thumbnail_url || ''}
        onImageUrlCommitted={(url) => saveMovie({ ...movie, thumbnail_url: url })}
        ariaLabel="Movie thumbnail"
        maxHeight={200}
        required
        readOnly={readOnly}
      />
      <Box component="fieldset" disabled={readOnly} sx={{ border: 'none', m: 0, p: 0, minWidth: 0 }}>
      {sourceClustersSlot ? <Box sx={{ mb: 2 }}>{sourceClustersSlot}</Box> : null}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {!accessInHeader ? (
            <TextField
              fullWidth
              required
              label="Movie title"
              value={movie.movie_title}
              onChange={(e) => saveMovie({ ...movie, movie_title: e.target.value })}
              error={titleMissing}
              helperText={titleMissing ? 'Required' : undefined}
              sx={{ ...fieldSx, mb: 2 }}
            />
          ) : null}
          <TextField
            fullWidth
            required
            label="Movie URL"
            value={movie.youtube_url}
            onChange={(e) => saveMovie({ ...movie, youtube_url: e.target.value })}
            error={urlMissing}
            helperText={urlMissing ? 'Required' : undefined}
            sx={{ ...fieldSx, mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ color: '#a1a1aa', mb: 1 }}>
            Rating
          </Typography>
          <Tabs
            value={ratingUiTab}
            onChange={handleRatingTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mb: 1.5,
              minHeight: 44,
              borderBottom: '1px solid #27272a',
              '& .MuiTab-root': {
                color: '#a1a1aa',
                minHeight: 44,
                py: 1,
                textTransform: 'none',
              },
              '& .Mui-selected': { color: '#fafafa' },
              '& .MuiTabs-indicator': { bgcolor: '#60a5fa' },
            }}
          >
            <Tab
              value={0}
              icon={
                <Box
                  component="img"
                  src={MOVIE_TAB_LOGO.imdb}
                  alt="IMDb"
                  sx={{ height: 16, width: 36, objectFit: 'contain', display: 'block' }}
                />
              }
              label=""
              aria-label="IMDb rating"
              sx={{ minWidth: 56, px: 1 }}
            />
            <Tab
              value={1}
              icon={
                <Box
                  component="img"
                  src={MOVIE_TAB_LOGO.rottenTomatoes}
                  alt="Rotten Tomatoes"
                  sx={{ height: 18, maxWidth: 100, width: 'auto', objectFit: 'contain', display: 'block' }}
                />
              }
              label=""
              aria-label="Rotten Tomatoes rating"
              sx={{ minWidth: 72, px: 1 }}
            />
            <Tab value={2} icon={<StarsIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Editor" />
          </Tabs>

          {(ratingUiTab === 0 || ratingUiTab === 1) && (
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'nowrap', width: '100%' }}>
                <Rating
                  name={`movie-source-rating-${externalSite}`}
                  value={tabSourceStarRating(movie, externalSite)}
                  precision={1}
                  max={5}
                  icon={<StarIcon fontSize="inherit" />}
                  emptyIcon={<StarBorderIcon fontSize="inherit" sx={{ color: '#a1a1aa' }} />}
                  onChange={(_, v) =>
                    saveMovie(saveSourceStarRating(movie, externalSite, v == null ? 0 : Number(v)))
                  }
                  sx={rowRatingSx}
                />
                <TextField
                  size="small"
                  fullWidth
                  hiddenLabel
                  placeholder="Link to movie review"
                  value={reviewUrlInputValue(movie, externalSite)}
                  onChange={(e) => saveMovie(saveReviewUrlForSite(movie, externalSite, e.target.value))}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    ...fieldSx,
                    '& .MuiOutlinedInput-root': {
                      height: REVIEW_ROW_HEIGHT,
                    },
                  }}
                  InputProps={{
                    sx: { height: REVIEW_ROW_HEIGHT, alignItems: 'center' },
                  }}
                />
                <Tooltip title="Open preview in a new tab">
                  <IconButton
                    type="button"
                    onClick={openReviewPreview}
                    aria-label="Open review or search preview"
                    sx={{
                      height: REVIEW_ROW_HEIGHT,
                      width: REVIEW_ROW_HEIGHT,
                      flexShrink: 0,
                      color: '#a1a1aa',
                      border: '1px solid #3f3f46',
                      borderRadius: 1,
                    }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="caption" sx={{ color: '#71717a', display: 'block', mt: 0.5 }}>
                Preview opens your link if set; otherwise the default search URL for the movie title.
              </Typography>
            </Box>
          )}

          {ratingUiTab === 2 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#a1a1aa', mb: 0.5 }}>
                Your rating (1–5 stars)
              </Typography>
              <Rating
                name="movie-star-rating"
                value={movie.rating > 0 ? movie.rating : null}
                precision={1}
                max={5}
                icon={<StarIcon fontSize="inherit" />}
                emptyIcon={<StarBorderIcon fontSize="inherit" sx={{ color: '#a1a1aa' }} />}
                onChange={(_, v) => saveMovie({ ...movie, rating: v == null ? 0 : Number(v) })}
                sx={{ ...rowRatingSx, mb: 0.5 }}
              />
            </Box>
          )}

          <Typography variant="subtitle2" sx={{ color: '#a1a1aa', mb: 1 }}>
            Age rating
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            {MOVIE_AGE_RATINGS.map((ar) => (
              <Chip
                key={ar}
                label={ar}
                onClick={() => saveMovie({ ...movie, age_rating: ar })}
                variant={movie.age_rating === ar ? 'filled' : 'outlined'}
                sx={{
                  borderColor: '#3f3f46',
                  color: movie.age_rating === ar ? '#0a0a0a' : '#fafafa',
                  bgcolor: movie.age_rating === ar ? '#60a5fa' : 'transparent',
                  '&:hover': { bgcolor: movie.age_rating === ar ? '#3b82f6' : '#27272a' },
                }}
              />
            ))}
          </Stack>

          <Typography variant="subtitle2" sx={{ color: '#a1a1aa', mb: 0.5 }}>
            Availability <Typography component="span" sx={{ color: '#f87171' }}>*</Typography>
          </Typography>
          <Typography variant="caption" sx={{ color: '#71717a', display: 'block', mb: 1 }}>
            Required — pick one option.
          </Typography>
          <FormGroup row sx={{ mb: 2, gap: 2 }}>
            {MOVIE_AVAILABILITY_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Checkbox
                    checked={availabilityNorm === opt.value}
                    onChange={() => {
                      if (availabilityNorm !== opt.value) {
                        saveMovie({ ...movie, availability: opt.value });
                      }
                    }}
                    sx={{
                      color: '#71717a',
                      py: 0,
                      '&.Mui-checked': { color: '#60a5fa' },
                    }}
                  />
                }
                label={opt.label}
                sx={{ color: '#fafafa', mr: 0 }}
              />
            ))}
          </FormGroup>
          <TextField
            fullWidth
            type="number"
            label="Duration (minutes)"
            value={movie.duration_minutes || ''}
            onChange={(e) =>
              saveMovie({ ...movie, duration_minutes: Math.max(0, parseInt(e.target.value, 10) || 0) })
            }
            sx={{ ...fieldSx, mb: 2 }}
          />
          <Tabs
            value={detailTab}
            onChange={(_, v) => setDetailTab(v)}
            sx={{
              mb: 1,
              borderBottom: '1px solid #27272a',
              '& .MuiTab-root': { color: '#a1a1aa', minHeight: 40 },
              '& .Mui-selected': { color: '#fafafa' },
              '& .MuiTabs-indicator': { bgcolor: '#60a5fa' },
            }}
          >
            <Tab label="Plot" />
            <Tab label="Notes" />
            <Tab label="Discussion questions" />
          </Tabs>
          {detailTab === 2 && (
            <Button
              variant="outlined"
              onClick={() => {
                const questions = autoGenerateDiscussionQuestions(movieRef.current.movie_title, movieRef.current.plot_summary);
                saveMovie({
                  ...movieRef.current,
                  discussion_questions: questions,
                  discussion_blocks: questionsToBlocks(questions),
                });
                setDiscussionEditorKey((key) => key + 1);
              }}
              sx={{ mb: 1, color: '#fafafa', borderColor: '#3f3f46' }}
            >
              Auto-generate questions
            </Button>
          )}
          <Box sx={{ mb: 2 }}>
            {detailTab === 0 && (
              <ArticleBodyEditorJs
                notebookId={`${movie.id || 'movie'}:plot`}
                readOnly={readOnly}
                seed={{ content: movie.plot_summary || '', content_blocks: movie.plot_blocks || [] }}
                onBlurValidation={() => {}}
                onBodyChange={(content, content_blocks) => {
                  saveMovie({ ...movieRef.current, plot_summary: content, plot_blocks: content_blocks });
                }}
              />
            )}
            {detailTab === 1 && (
              <ArticleBodyEditorJs
                notebookId={`${movie.id || 'movie'}:notes`}
                readOnly={readOnly}
                seed={{ content: movie.notes || '', content_blocks: movie.notes_blocks || [] }}
                onBlurValidation={() => {}}
                onBodyChange={(content, content_blocks) => {
                  saveMovie({ ...movieRef.current, notes: content, notes_blocks: content_blocks });
                }}
              />
            )}
            {detailTab === 2 && (
              <ArticleBodyEditorJs
                notebookId={`${movie.id || 'movie'}:discussion:${discussionEditorKey}`}
                readOnly={readOnly}
                seed={{
                  content: (movie.discussion_questions || []).join('\n\n'),
                  content_blocks: movie.discussion_blocks || [],
                }}
                onBlurValidation={() => {}}
                onBodyChange={(_content, content_blocks) => {
                  const questions = content_blocks.map((block) => (block.text || '').trim()).filter(Boolean);
                  saveMovie({
                    ...movieRef.current,
                    discussion_questions: questions,
                    discussion_blocks: content_blocks,
                  });
                }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
}
